import { REACTIONS } from '../../../packages/domain/src/10-reactions.mjs';

const mountedRecords = new WeakMap();
const provisional = new WeakMap();

function ownReaction(record, actor) {
  return record.myReaction ?? (record.likes?.includes(actor) ? 'like' : null);
}

/** Provisional presentation only; accepted state and preference evidence stay server owned. */
export function previewReaction(record, actor, reaction) {
  const before = ownReaction(record, actor);
  const counts = { ...record.reactionCounts };
  if (before) counts[before] = Math.max(0, (counts[before] ?? 1) - 1);
  if (reaction) counts[reaction] = (counts[reaction] ?? 0) + 1;
  return {
    ...record,
    myReaction: reaction,
    likes: undefined,
    reactionCounts: counts,
    totalReactions: Math.max(
      0,
      (record.totalReactions ?? record.likes?.length ?? 0) +
        Number(Boolean(reaction)) -
        Number(Boolean(before)),
    ),
  };
}

/** A stationary 500ms hold opens choices; sliding afterward highlights a target and
 * releasing selects it. Outside release keeps untimed tap choices; cancel never selects.
 * Injected timer functions make cancellation and synthetic-click suppression testable. */
export function bindReactionGestures(trigger, open, timers = {}) {
  const schedule = timers.schedule ?? setTimeout;
  const cancel = timers.cancel ?? clearTimeout;
  let pointer,
    timer,
    suppressClick = false,
    origin,
    opened = false,
    moved = false,
    picker;
  const stop = () => {
    cancel(timer);
    timer = undefined;
    if (pointer && trigger.hasPointerCapture?.(pointer.id))
      trigger.releasePointerCapture(pointer.id);
    pointer = undefined;
  };
  trigger.addEventListener('pointerdown', (event) => {
    picker?.cancel();
    picker = undefined;
    stop();
    suppressClick = false;
    origin = undefined;
    opened = moved = false;
    if (
      event.isPrimary === false ||
      event.button !== 0 ||
      trigger.disabled ||
      trigger.getAttribute('aria-disabled') === 'true'
    )
      return;
    origin = { x: event.clientX, y: event.clientY };
    pointer = { id: event.pointerId, ...origin };
    timer = schedule(() => {
      timer = undefined;
      suppressClick = true;
      opened = true;
      picker = open(origin);
      if (picker && pointer) {
        picker.hold?.();
        trigger.setPointerCapture?.(pointer.id);
      }
    }, 500);
  });
  trigger.addEventListener('pointermove', (event) => {
    if (!pointer || pointer.id !== event.pointerId) return;
    if (opened && picker) {
      picker.move({ x: event.clientX, y: event.clientY });
      return;
    }
    if (Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) > 10) {
      suppressClick = true;
      moved = true;
      stop();
    }
  });
  for (const type of ['pointerup', 'pointercancel', 'pointerleave', 'lostpointercapture']) {
    trigger.addEventListener(type, (event) => {
      if (pointer && pointer.id !== event.pointerId) return;
      if (type === 'pointerleave' && opened && picker) return;
      if (type === 'pointerup' && pointer && opened && picker)
        picker.release({ x: event.clientX, y: event.clientY });
      else if (type !== 'pointerup') picker?.cancel();
      if (type === 'pointercancel' || type === 'lostpointercapture') {
        suppressClick = true;
        if (pointer && Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) > 10)
          moved = true;
      }
      if (type === 'pointerleave' && pointer) moved = true;
      stop();
      picker = undefined;
    });
  }
  trigger.addEventListener('contextmenu', (event) => {
    event.preventDefault();
    // Keep an already-open captured hold available for slide/release.
    if (opened && picker && pointer) return;
    stop();
    // Safari may cancel the pointer before delivering its native long-press menu.
    // Cancellation stops the timer, but must not hide a stationary hold's choices.
    if (!opened && !moved) {
      const point =
        Number.isFinite(event.clientX) &&
        Number.isFinite(event.clientY) &&
        (event.clientX !== 0 || event.clientY !== 0)
          ? { x: event.clientX, y: event.clientY }
          : origin;
      opened = true;
      picker = open(point);
    }
    suppressClick = true;
  });
  trigger.addEventListener(
    'click',
    (event) => {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    { capture: true },
  );
  trigger.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || (event.key === 'F10' && event.shiftKey)) {
      event.preventDefault();
      picker?.cancel();
      picker = undefined;
      stop();
      origin = undefined;
      opened = moved = false;
      open();
    } else if (event.key === 'Enter' || event.key === ' ') {
      stop();
      suppressClick = false;
    }
  });
  return () => {
    const suppressed = suppressClick;
    suppressClick = false;
    return suppressed;
  };
}

/** Reconcile mounted controls from the accepted actor snapshot, including dialog controls.
 * Weak records retain no detached DOM; every resolver reads current canonical state.
 * Decorative updates preserve media playback, reply drafts and focused button identity. */
export function reconcileReactionControls(ctx) {
  const root = ctx.view?.ownerDocument ?? (typeof document === 'undefined' ? null : document);
  for (const trigger of root?.querySelectorAll('.reaction-trigger') ?? []) {
    const binding = mountedRecords.get(trigger);
    if (!binding || binding.ctx !== ctx || binding.actor !== ctx.state?.me.id) continue;
    const record = binding.current();
    if (record) {
      const pending = provisional.get(ctx)?.get(binding.key);
      updateReactionControl(
        ctx,
        trigger,
        pending?.actor === binding.actor
          ? previewReaction(record, binding.actor, pending.reaction)
          : record,
      );
      trigger.setAttribute('aria-busy', String(Boolean(pending?.actor === binding.actor)));
    }
  }
}

/** Update only the stable button's decorative contents, preserving focus and neighboring media. */
export function updateReactionControl(ctx, button, record) {
  const own = ownReaction(record, ctx.state.me.id);
  const total = record.totalReactions ?? record.likes?.length ?? 0;
  const chosen = REACTIONS.find(({ id }) => id === own);
  const label = ctx.uiText(chosen?.label ?? 'Like');
  button.setAttribute('aria-label', `${label} · ${total}`);
  button.title = `${label} · ${total} · ${ctx.uiText('Hold or press Arrow Down for reactions')}`;
  button.setAttribute('aria-pressed', String(Boolean(own)));
  const summary = ctx.e('span', 'reaction-summary');
  const shown = chosen
    ? [chosen]
    : REACTIONS.filter(({ id }) => (record.reactionCounts?.[id] ?? 0) > 0).slice(0, 3);
  if (!shown.length) summary.append(ctx.icon('thumbs-up'));
  for (const reaction of shown) {
    const emoji = ctx.e('span', 'reaction-emoji', reaction.emoji);
    emoji.setAttribute('aria-hidden', 'true');
    summary.append(emoji);
  }
  button.replaceChildren(summary, ctx.e('span', 'action-count', String(total)));
  button.dataset.myReaction = own ?? '';
  button.dataset.reactionCount = String(total);
}

/** One shared default Like toggle and hold/keyboard picker. Commands carry desired state;
 * provisional feedback never writes canonical snapshots or preference evidence. */
export function createReactionControl(ctx, { record, current, command, key }) {
  let suppressClick = () => false;
  const actor = ctx.state?.me.id;
  const trigger = ctx.iconButton('thumbs-up', 'Like', async () => {
    if (suppressClick()) return;
    const latest = current();
    const pending = provisional.get(ctx)?.get(key);
    const own = pending?.actor === actor ? pending.reaction : latest && ownReaction(latest, actor);
    if (latest) await select(own ? null : 'like');
  });
  trigger.concurrentAction = true;
  trigger.classList.add('reaction-trigger');
  trigger.dataset.focusKey = `reaction-${key}`;
  mountedRecords.set(trigger, { ctx, current, actor, key });
  trigger.setAttribute('aria-haspopup', 'dialog');
  trigger.setAttribute('aria-expanded', 'false');
  const scope = {
    'comment-reaction': 'comments',
    'message-reaction': 'messages',
    'group-reaction': 'circles',
  }[command.type];
  trigger.disabled = Boolean(
    ctx.featureEnabled?.('reactions') === false || (scope && ctx.featureEnabled?.(scope) === false),
  );
  async function select(reaction) {
    if (trigger.isConnected === false || trigger.disabled || ctx.state?.me.id !== actor) return;
    let pending = provisional.get(ctx);
    if (!pending) provisional.set(ctx, (pending = new Map()));
    const active = pending.get(key);
    if (active?.actor === actor) {
      active.reaction = reaction;
      updateReactionControl(ctx, trigger, previewReaction(current(), actor, reaction));
      reconcileReactionControls(ctx);
      return active.promise;
    }
    const before = current();
    if (!before) throw new Error(ctx.uiText('This item is unavailable.'));
    const entry = { actor, reaction, preview: previewReaction(before, actor, reaction) };
    pending.set(key, entry);
    updateReactionControl(ctx, trigger, entry.preview);
    trigger.setAttribute('aria-busy', 'true');
    reconcileReactionControls(ctx);
    entry.promise = (async () => {
      try {
        let submitted;
        do {
          submitted = entry.reaction;
          await ctx.command({ ...command, reaction: submitted }, { paint: false });
          if (ctx.state?.me.id !== actor) break;
        } while (entry.reaction !== submitted);
      } finally {
        if (pending.get(key) === entry) pending.delete(key);
        if (ctx.state?.me.id === actor) {
          const accepted = current();
          if (accepted) updateReactionControl(ctx, trigger, accepted);
          trigger.setAttribute('aria-busy', 'false');
          reconcileReactionControls(ctx);
        } else {
          const root = ctx.view?.ownerDocument;
          for (const mounted of root?.querySelectorAll('.reaction-trigger') ?? [trigger]) {
            const binding = mountedRecords.get(mounted);
            if (binding?.ctx === ctx && binding.actor === actor && binding.key === key)
              updateReactionControl(ctx, mounted, {
                ...before,
                myReaction: ownReaction(before, actor),
                likes: undefined,
              });
          }
        }
      }
    })();
    return entry.promise;
  }
  function open(point) {
    if (
      trigger.isConnected === false ||
      trigger.disabled ||
      ctx.state?.me.id !== actor ||
      provisional.get(ctx)?.get(key)?.actor === actor
    )
      return;
    const latest = current();
    if (!latest) return;
    const body = ctx.e('div', 'reaction-picker');
    const choices = ctx.e('div', 'reaction-choices');
    choices.setAttribute('role', 'group');
    choices.setAttribute('aria-label', ctx.uiText('Reactions'));
    let close = () => {},
      highlighted;
    const labelView = ctx.e('span', 'reaction-picker-label', ctx.uiText('Reactions'));
    labelView.setAttribute('role', 'status');
    labelView.setAttribute('aria-live', 'polite');
    const highlight = (choice) => {
      if (highlighted === choice) return;
      if (highlighted) highlighted.dataset.highlighted = 'false';
      highlighted = choice;
      if (choice) choice.dataset.highlighted = 'true';
      labelView.textContent = choice?.dataset.selectionLabel ?? ctx.uiText('Reactions');
    };
    for (const reaction of REACTIONS) {
      const choice = ctx.button(
        '',
        async () => {
          const before = current();
          if (!before) throw new Error(ctx.uiText('This item is unavailable.'));
          const own = ownReaction(before, actor);
          close();
          await select(own === reaction.id ? null : reaction.id);
        },
        'palette-choice reaction-choice',
      );
      const label = ctx.uiText(reaction.label);
      choice.dataset.selectionLabel =
        ownReaction(latest, actor) === reaction.id
          ? `${label} · ${ctx.uiText('Remove reaction')}`
          : label;
      choice.addEventListener('pointerenter', () => highlight(choice));
      choice.addEventListener('focus', () => highlight(choice));
      const count =
        latest.reactionCounts?.[reaction.id] ??
        (reaction.id === 'like' ? (latest.likes?.length ?? 0) : 0);
      const accessibleLabel = `${label} · ${count}`;
      choice.setAttribute('aria-label', accessibleLabel);
      choice.title = accessibleLabel;
      choice.setAttribute(
        'aria-pressed',
        String(
          (latest.myReaction ?? (latest.likes?.includes(ctx.state.me.id) ? 'like' : null)) ===
            reaction.id,
        ),
      );
      const emoji = ctx.e('span', 'reaction-emoji', reaction.emoji);
      emoji.setAttribute('aria-hidden', 'true');
      choice.append(emoji);
      if (count > 0) choice.append(ctx.e('span', 'reaction-choice-count', String(count)));
      choices.append(choice);
    }
    choices.addEventListener('keydown', (event) => {
      if (!['ArrowUp', 'ArrowDown'].includes(event.key)) return;
      const buttons = [...choices.children];
      const index = buttons.indexOf(choices.ownerDocument?.activeElement);
      if (index < 0) return;
      event.preventDefault();
      const narrow = choices.ownerDocument?.defaultView?.matchMedia('(max-width: 359px)').matches;
      const step = narrow ? 4 : 1;
      buttons[
        Math.max(
          0,
          Math.min(buttons.length - 1, index + (event.key === 'ArrowDown' ? step : -step)),
        )
      ]?.focus();
    });
    choices.addEventListener('pointerleave', () => highlight(undefined));
    body.append(choices, labelView);
    close = ctx.contextMenu(trigger, 'Reactions', body, point ?? { anchor: true });
    let restoreInvoker;
    const finishHold = () => {
      const restore = restoreInvoker;
      restoreInvoker = undefined;
      // Native light-dismiss runs for this pointerup. Keep the association through
      // event dispatch and its synthetic click, then restore ordinary Like behavior.
      if (restore) setTimeout(restore, 0);
    };
    const move = (point) => {
      if (
        body.isConnected === false ||
        trigger.isConnected === false ||
        ctx.state?.me.id !== actor
      ) {
        highlight(undefined);
        return;
      }
      highlight(
        [...choices.children].find((choice) => {
          const bounds = choice.getBoundingClientRect();
          return (
            bounds.right > bounds.left &&
            bounds.bottom > bounds.top &&
            point.x >= bounds.left &&
            point.x <= bounds.right &&
            point.y >= bounds.top &&
            point.y <= bounds.bottom
          );
        }),
      );
    };
    return {
      hold() {
        const palette = body.closest?.('[popover]');
        if (!palette || !('popoverTargetElement' in trigger)) return;
        const previous = trigger.popoverTargetElement;
        trigger.popoverTargetElement = palette;
        restoreInvoker = () => {
          if (trigger.popoverTargetElement === palette) trigger.popoverTargetElement = previous;
        };
      },
      move,
      release(point) {
        move(point);
        const selected = highlighted;
        highlight(undefined);
        selected?.click();
        finishHold();
      },
      cancel() {
        highlight(undefined);
        finishHold();
      },
    };
  }
  suppressClick = bindReactionGestures(trigger, open);
  updateReactionControl(ctx, trigger, record);
  return trigger;
}
